import clsx from 'clsx';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { BiSolidChevronDown } from 'react-icons/bi';

import { Input } from '@/components/Inputs';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import {
  convertCurrency,
  convertLength,
  convertPerVolume,
  convertSpeed,
  convertVolume,
  round1,
  round2,
  SPEED_UNITS,
  type CurrencyUnit,
  type LengthUnit,
  type SpeedUnit,
  type VolumeUnit,
} from '@/utils/unitConversion';

import { NumberArrows } from './NumberArrows';
import s from './UnitInput.module.scss';

/**
 * `perVolume` is a price per volume unit: it follows the volume preference, converted inversely.
 * `speed` has no saved preference: the field is pinned to its record's unit through `unit`.
 */
type UnitKind = 'length' | 'volume' | 'perVolume' | 'currency' | 'speed';

const UNIT_OPTIONS: Record<UnitKind, string[]> = {
  length: ['ft', 'm'],
  volume: ['gal', 'L'],
  perVolume: ['gal', 'L'],
  currency: ['$', '€'],
  speed: SPEED_UNITS,
};

const useCurrentUnit = (kind: UnitKind): string => {
  const { lengthUnit, volumeUnit, currencyUnit } = useUnitPreferences();
  return {
    length: lengthUnit,
    volume: volumeUnit,
    perVolume: volumeUnit,
    currency: currencyUnit,
    speed: 'kn',
  }[kind];
};

// 32-px options + 16 px vertical padding.
const dropdownHeight = (kind: UnitKind) => UNIT_OPTIONS[kind].length * 32 + 16;

// Mirrors the usePopoutStyle pattern from QuizDropdown.tsx. Measures the
// trigger with getBoundingClientRect so the portal <ul> aligns to it exactly,
// avoiding the stacking-context cap that occurs when the list is rendered
// inside Input's .suffixInteractive span (z-index:5 ceiling).
const usePopoutStyle = (
  anchorRef: React.RefObject<HTMLElement | null>,
  opened: boolean,
  maxHeight: number,
): CSSProperties | undefined => {
  const [style, setStyle] = useState<CSSProperties>();

  useLayoutEffect(() => {
    if (!opened) return;

    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const upwards = spaceBelow < maxHeight && rect.top > spaceBelow;
      setStyle({
        left: rect.left,
        minWidth: rect.width,
        ...(upwards ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef, opened, maxHeight]);

  return style;
};

type UnitButtonProps = {
  kind: UnitKind;
  currentUnit: string;
  /** Called with the chosen unit; value conversion happens via the unit-change effect. */
  onSwitch: (next: string) => void;
  /** Preference not loaded yet — switching now could not be persisted safely. */
  disabled?: boolean;
};

function UnitButton({ kind, currentUnit, onSwitch, disabled }: UnitButtonProps) {
  const [opened, setOpened] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const containerRef = useClickOutside<HTMLDivElement>((event) => {
    if (dropdownRef.current?.contains(event.target as Node)) return;
    setOpened(false);
  });
  const popoutStyle = usePopoutStyle(buttonRef, opened, dropdownHeight(kind));

  return (
    <div ref={containerRef} className={s.buttonContainer}>
      <button
        ref={buttonRef}
        type="button"
        className={s.unitButton}
        aria-haspopup="listbox"
        aria-expanded={opened}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpened((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpened((v) => !v);
          }
          if (e.key === 'Escape') setOpened(false);
        }}
      >
        {currentUnit}
        <BiSolidChevronDown />
      </button>
      {opened &&
        createPortal(
          <ul className={s.dropdown} style={popoutStyle} role="listbox" ref={dropdownRef}>
            {UNIT_OPTIONS[kind].map((opt) => (
              <li
                key={opt}
                role="option"
                aria-selected={opt === currentUnit}
                className={clsx(s.option, opt === currentUnit && s.active)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSwitch(opt);
                  setOpened(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSwitch(opt);
                    setOpened(false);
                  }
                }}
                tabIndex={0}
              >
                {opt}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}

export type UnitInputProps = {
  kind: UnitKind;
  label?: string;
  error?: string;
  mandatory?: boolean;
  className?: string;
  value: string;
  onChange: (displayValue: string) => void;
  /**
   * compact=true — renders a bare input + inline unit button with no label /
   * error chrome, matching the existing raw-input aesthetic in NavFooterSection.
   */
  compact?: boolean;
  /**
   * Opt out of the automatic in-place value conversion on unit change. For
   * call sites where a page-level effect converts the whole state at once
   * (fields that live in steps/sections that may be unmounted when the unit
   * switches, e.g. the onboarding wizard) — otherwise the value would be
   * converted twice.
   */
  noAutoConvert?: boolean;
  /**
   * Pins the field to this unit instead of the user's saved preference, for a
   * record that keeps its own unit (the oil log). Picking another unit then
   * calls onUnitChange and saves no preference; the owner passes the new unit
   * back, and the value converts as usual.
   */
  unit?: string;
  onUnitChange?: (next: string) => void;
} & Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'value' | 'onChange' | 'suffix'
>;

/**
 * Numeric input with an interactive unit-selector dropdown in the suffix
 * position. value/onChange are in the display (preferred) unit.
 *
 * The unit preference is global per quantity kind; picking a unit here
 * persists it. Value conversion is NOT done in the click handler — every
 * mounted UnitInput (this one included) reacts to the unit change through the
 * effect below, so sibling fields on the same screen convert in lockstep.
 * The same effect heals the late-loading case: values seeded under the
 * default unit are converted in place when the stored preference arrives.
 */
export function UnitInput({
  kind,
  value,
  onChange,
  label,
  error,
  mandatory,
  className,
  compact,
  noAutoConvert,
  unit,
  onUnitChange,
  ...rest
}: UnitInputProps) {
  const { isLoaded, setLengthUnit, setVolumeUnit, setCurrencyUnit } = useUnitPreferences();
  const preferredUnit = useCurrentUnit(kind);
  const currentUnit = unit ?? preferredUnit;
  // A pinned unit saves nothing, so it need not wait for the preference to load.
  const switchDisabled = unit === undefined && !isLoaded;

  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const prevUnitRef = useRef(currentUnit);

  // Keep the refs in sync after every commit; declared before the conversion
  // effect so they are already current when it runs.
  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const prev = prevUnitRef.current;
    if (prev === currentUnit) return;
    prevUnitRef.current = currentUnit;
    if (noAutoConvert) return;
    const parsed = parseFloat(valueRef.current);
    if (isNaN(parsed)) return;
    if (kind === 'currency') {
      const converted = convertCurrency(parsed, prev as CurrencyUnit, currentUnit as CurrencyUnit);
      onChangeRef.current(String(round2(converted)));
      return;
    }
    if (kind === 'speed') {
      const converted = convertSpeed(parsed, prev as SpeedUnit, currentUnit as SpeedUnit);
      onChangeRef.current(String(round1(converted)));
      return;
    }
    if (kind === 'perVolume') {
      // A price: rounded to cents, like money.
      const converted = convertPerVolume(parsed, prev as VolumeUnit, currentUnit as VolumeUnit);
      onChangeRef.current(String(round2(converted)));
      return;
    }
    const converted =
      kind === 'length'
        ? convertLength(parsed, prev as LengthUnit, currentUnit as LengthUnit)
        : convertVolume(parsed, prev as VolumeUnit, currentUnit as VolumeUnit);
    onChangeRef.current(String(round1(converted)));
  }, [currentUnit, kind, noAutoConvert]);

  const handleSwitch = (next: string) => {
    if (onUnitChange) onUnitChange(next);
    else if (kind === 'speed') return;
    else if (kind === 'length') setLengthUnit(next as LengthUnit);
    else if (kind === 'volume' || kind === 'perVolume') setVolumeUnit(next as VolumeUnit);
    else setCurrencyUnit(next as CurrencyUnit);
  };

  if (compact) {
    return (
      <div className={clsx(s.compactWrapper, className)}>
        <input
          className={s.compactInput}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          {...rest}
        />
        {rest.type === 'number' && (
          <NumberArrows
            value={value}
            onChange={onChange}
            min={rest.min}
            step={rest.step}
            disabled={rest.disabled}
            name={rest['aria-label'] ?? label}
          />
        )}
        <CompactUnitButton
          kind={kind}
          currentUnit={currentUnit}
          onSwitch={handleSwitch}
          disabled={switchDisabled}
        />
      </div>
    );
  }

  return (
    <Input
      label={label}
      error={error}
      mandatory={mandatory}
      className={className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      suffixNode={
        <UnitButton
          kind={kind}
          currentUnit={currentUnit}
          onSwitch={handleSwitch}
          disabled={switchDisabled}
        />
      }
      {...rest}
    />
  );
}

// ── Compact variant button ────────────────────────────────────────────────────

type CompactUnitButtonProps = {
  kind: UnitKind;
  currentUnit: string;
  onSwitch: (next: string) => void;
  disabled?: boolean;
};

function CompactUnitButton({ kind, currentUnit, onSwitch, disabled }: CompactUnitButtonProps) {
  const [opened, setOpened] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const containerRef = useClickOutside<HTMLDivElement>((event) => {
    if (dropdownRef.current?.contains(event.target as Node)) return;
    setOpened(false);
  });
  const popoutStyle = usePopoutStyle(buttonRef, opened, dropdownHeight(kind));

  return (
    <div ref={containerRef} className={s.compactButtonContainer}>
      <button
        ref={buttonRef}
        type="button"
        className={s.compactUnitButton}
        aria-haspopup="listbox"
        aria-expanded={opened}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpened((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpened((v) => !v);
          }
          if (e.key === 'Escape') setOpened(false);
        }}
      >
        {currentUnit}
        <BiSolidChevronDown />
      </button>
      {opened &&
        createPortal(
          <ul className={s.dropdown} style={popoutStyle} role="listbox" ref={dropdownRef}>
            {UNIT_OPTIONS[kind].map((opt) => (
              <li
                key={opt}
                role="option"
                aria-selected={opt === currentUnit}
                className={clsx(s.option, opt === currentUnit && s.active)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSwitch(opt);
                  setOpened(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSwitch(opt);
                    setOpened(false);
                  }
                }}
                tabIndex={0}
              >
                {opt}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
